import React, { useState, useEffect } from 'react';
import './VersionDisplay.css';

/**
 * Version Display Component
 * Shows current app version, release date, and changelog
 */
const VersionDisplay = ({ compact = false }) => {
  const [version, setVersion] = useState(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load version info
    fetch('/VERSION.json')
      .then(res => res.json())
      .then(data => {
        setVersion(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading version:', err);
        setVersion({
          version: '2.0.0',
          codename: 'RBAC Implementation',
          stage: 'development'
        });
        setLoading(false);
      });
  }, []);

  if (loading || !version) {
    return null;
  }

  if (compact) {
    return (
      <div className="version-compact">
        <button 
          className="version-btn"
          onClick={() => setShowChangelog(true)}
          title="View version details"
        >
          v{version.version} {version.stage === 'development' && '(dev)'}
        </button>
        
        {showChangelog && (
          <ChangelogModal 
            version={version} 
            onClose={() => setShowChangelog(false)} 
          />
        )}
      </div>
    );
  }

  return (
    <div className="version-display">
      <div className="version-header">
        <span className="version-number">v{version.version}</span>
        {version.codename && (
          <span className="version-codename">"{version.codename}"</span>
        )}
        <span className={`version-stage ${version.stage}`}>
          {version.stage}
        </span>
      </div>
      {version.releaseDate && (
        <div className="version-date">
          Released: {new Date(version.releaseDate).toLocaleDateString()}
        </div>
      )}
      <button 
        className="version-changelog-btn"
        onClick={() => setShowChangelog(true)}
      >
        View Changelog
      </button>

      {showChangelog && (
        <ChangelogModal 
          version={version} 
          onClose={() => setShowChangelog(false)} 
        />
      )}
    </div>
  );
};

/**
 * Changelog Modal
 * Displays detailed changelog for current version
 */
const ChangelogModal = ({ version, onClose }) => {
  if (!version || !version.changelog) {
    return null;
  }

  const currentVersion = version.changelog[version.version];

  if (!currentVersion) {
    return null;
  }

  return (
    <div className="changelog-modal-overlay" onClick={onClose}>
      <div className="changelog-modal" onClick={e => e.stopPropagation()}>
        <div className="changelog-header">
          <div className="changelog-title">
            <h2>Version {version.version}</h2>
            <span className="changelog-codename">"{version.codename}"</span>
          </div>
          <button className="changelog-close" onClick={onClose}>×</button>
        </div>

        <div className="changelog-content">
          <div className="changelog-meta">
            <span className="changelog-date">
              📅 {new Date(currentVersion.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className={`changelog-type ${currentVersion.type}`}>
              {currentVersion.type === 'major' ? '🚀 Major Update' : 
               currentVersion.type === 'minor' ? '✨ Minor Update' : 
               '🔧 Patch'}
            </span>
          </div>

          <div className="changelog-description">
            <h3>{currentVersion.title}</h3>
            <p>{currentVersion.description}</p>
          </div>

          {currentVersion.features && currentVersion.features.length > 0 && (
            <div className="changelog-section">
              <h4>✨ What's New</h4>
              <ul>
                {currentVersion.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          {currentVersion.breaking && currentVersion.breaking.length > 0 && (
            <div className="changelog-section changelog-breaking">
              <h4>⚠️ Breaking Changes</h4>
              <ul>
                {currentVersion.breaking.map((change, index) => (
                  <li key={index}>{change}</li>
                ))}
              </ul>
            </div>
          )}

          {currentVersion.notes && (
            <div className="changelog-section changelog-notes">
              <h4>📝 Notes</h4>
              <p>{currentVersion.notes}</p>
            </div>
          )}

          <div className="changelog-footer">
            <p>
              For full changelog and version history, see{' '}
              <a 
                href="/CHANGELOG.md" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                CHANGELOG.md
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionDisplay;

